import Add16 from '@carbon/icons-react/es/add/16';
import { Table as AntdTable } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Squad } from 'api/models';
import { Button } from 'components/atoms/button';
import { Empty } from 'components/atoms/empty';
import { NewRow } from 'components/molecules/new-row';
import styles from 'components/molecules/tasks/task-table/tasks-table.module.css';
import { ProjectTemplateTask } from 'data/templates/projects';
import { useTableConfig } from './table-config';

const Table = styled(AntdTable)`
  .ant-table {
    z-index: unset;
  }
`;

const rowClassName = (record: Partial<ProjectTemplateTask>): string => {
  return `${styles.row} ${record.private ? 'private' : ''}`;
};

export const DocumentTemplateTaskList: FC<{
  className?: string;
  data?: Partial<ProjectTemplateTask>[];
  docId: number;
  resourceSectionId: number;
  isPrivate?: boolean;
  readOnly?: boolean;
  handleAssigneeChange(taskId: number, selected: Squad): void;
  handleNewTaskAdded: (taskName: string) => Promise<void>;
  onEnableTaskAddRow?: () => void;
  onDelete(record: ProjectTemplateTask): void;
}> = ({
  className,
  data = [],
  docId,
  isPrivate,
  resourceSectionId,
  readOnly,
  handleAssigneeChange,
  handleNewTaskAdded,
  onEnableTaskAddRow,
  onDelete,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const columnConfig = useTableConfig(
    docId,
    resourceSectionId,
    handleAssigneeChange,
    onDelete,
    readOnly,
  );
  const [canShowEmpty, setCanShowEmpty] = useState<boolean>(!data.length);

  useEffect(() => {
    setCanShowEmpty(!data.length);
  }, [data.length]);

  useEffect(() => {
    // Create section on mount so that it will reflect on the right side pane properly and BE knows about this.
    if (canShowEmpty && onEnableTaskAddRow) {
      onEnableTaskAddRow();
    }
  }, [canShowEmpty, onEnableTaskAddRow]);

  const _onEnableTaskAddRow = () => {
    setCanShowEmpty(false);

    if (onEnableTaskAddRow) {
      onEnableTaskAddRow();
    }
  };

  return (
    <div className={isPrivate ? styles.private : ''}>
      {!canShowEmpty ? (
        <div ref={tableRef} className={className}>
          {data.length > 0 && (
            <Table
              rowKey="taskId"
              pagination={false}
              columns={columnConfig}
              dataSource={data}
              className={styles.table}
              rowClassName={rowClassName}
              tableLayout="fixed"
            />
          )}
          {!readOnly && (
            <NewRow
              onAdd={handleNewTaskAdded}
              placeholder="Add a new task"
              bulkAddType="tasks"
            />
          )}
        </div>
      ) : (
        <Empty hideImage isPrivate={isPrivate}>
          <Button
            ref={tableRef}
            onClick={_onEnableTaskAddRow}
            type="link"
            size="small"
            icon={<Add16 />}
          >
            <FormattedMessage id="task.add" />
          </Button>
        </Empty>
      )}
    </div>
  );
};
